import { useState } from 'react'
import { AlertCircle, ChevronLeft, FileText } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useActiveContract } from '@/features/auth/hooks/useActiveContract'
import { useBollette, useDettaglioBolletta } from '@/lib/graphql/hooks'
import { cn } from '@/lib/utils'
import { DetailPeriod } from './components/DetailPeriod'

/**
 * Pagina "Dettaglio voci per periodo".
 *
 * Replica del mobile `InvoiceDetails.jsx`:
 *   - se la bolletta è bimestrale (2 periodi) mostra tab bar in cima
 *   - se mensile (1 periodo) render diretto
 *   - empty state se backend ritorna "false" (dettaglio non disponibile)
 *   - error state se la query fallisce per altri motivi
 */
export function InvoiceVociPage() {
  const { codice } = useParams<{ codice: string }>()
  const { cliente, activeContract } = useActiveContract()

  const codiceCliente = cliente?.codice_cliente
  const codiceContratto = activeContract?.codice_contratto

  const bolletteQ = useBollette(codiceCliente, codiceContratto)
  const detailQ = useDettaglioBolletta(codiceCliente, codiceContratto, codice)

  const invoice = bolletteQ.data?.find((b) => b.codice === codice)
  const totaleBolletta = Number(invoice?.imponibile || 0)

  const [activeTab, setActiveTab] = useState(0)

  const isLoading = bolletteQ.isLoading || detailQ.isLoading
  const error = detailQ.error

  const backLink = (
    <Link
      to={`/bollette/${encodeURIComponent(codice ?? '')}`}
      className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-secondary hover:underline"
    >
      <ChevronLeft className="h-4 w-4" />
      Bolletta {codice ?? ''}
    </Link>
  )

  const header = (
    <header>
      <h1 className="text-2xl font-bold text-foreground md:text-3xl">
        Dettaglio voci
      </h1>
      <p className="text-sm text-muted-foreground">
        Composizione voci della bolletta per periodo.
      </p>
    </header>
  )

  if (error) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        {backLink}
        {header}
        <EmptyState
          icon={<AlertCircle className="h-5 w-5 text-destructive" />}
          title="Si è verificato un errore"
          description={error.message}
        />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        {backLink}
        {header}
        <DetailSkeleton />
      </div>
    )
  }

  const result = detailQ.data
  if (!result || result.unavailable || !result.dettaglio) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        {backLink}
        {header}
        <EmptyState
          icon={<FileText className="h-5 w-5" />}
          title="Dettaglio non disponibile"
          description="Per questa bolletta non è disponibile il dettaglio delle voci per periodo."
        />
      </div>
    )
  }

  const periods = Object.keys(result.dettaglio)
  if (periods.length === 0) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        {backLink}
        {header}
        <EmptyState
          icon={<FileText className="h-5 w-5" />}
          title="Dettaglio non disponibile"
          description="Dettaglio voci non disponibile per questa bolletta."
        />
      </div>
    )
  }

  const isBimestrale = periods.length > 1
  const currentIdx = isBimestrale ? activeTab : 0
  const currentPeriodLabel = periods[currentIdx]
  const currentPeriod = result.dettaglio[currentPeriodLabel]

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      {backLink}
      {header}

      {isBimestrale && (
        <nav
          className="flex rounded-xl border border-border bg-card"
          aria-label="Periodi della bolletta"
        >
          {periods.map((p, idx) => {
            const isActive = idx === activeTab
            return (
              <button
                key={p}
                type="button"
                onClick={() => setActiveTab(idx)}
                className={cn(
                  'flex-1 px-4 py-3 text-sm font-medium transition-colors',
                  'first:rounded-l-xl last:rounded-r-xl',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'bg-secondary/10 font-bold text-secondary'
                    : 'text-muted-foreground hover:bg-accent/50',
                )}
                aria-pressed={isActive}
              >
                {p}
              </button>
            )
          })}
        </nav>
      )}

      <DetailPeriod
        periodLabel={currentPeriodLabel}
        period={currentPeriod}
        totaleBolletta={totaleBolletta}
      />
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <Skeleton className="h-4 w-48" />
        <div className="mt-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-2.5 w-2.5 rounded-full" />
              <Skeleton className="h-3 flex-1" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
        </div>
      </Card>
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-5 w-24" />
        </div>
      </Card>
      <Card className="p-5">
        <Skeleton className="mb-4 h-4 w-32" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-7 w-7 rounded-md" />
              <Skeleton className="h-2.5 flex-1 rounded-full" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
